import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useInterview } from '@/contexts/InterviewContext'
import { FileText, PhoneCall, Video } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'

const Interview = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { setInterviewData } = useInterview()

  const [showDialog, setShowDialog] = useState(false)
  const [interviewType, setInterviewType] = useState<'verbal' | 'video' | null>(null)
  const [candidateName, setCandidateName] = useState('')
  const [candidateEmail, setCandidateEmail] = useState('')

  const handleStartInterview = (type: 'verbal' | 'video') => {
    setInterviewType(type)
    setShowDialog(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!candidateName.trim() || !candidateEmail.trim()) {
      toast({
        title: 'Bilgiler Eksik',
        description: 'Lütfen ad, soyad ve e-posta adresinizi girin.',
        variant: 'destructive',
      })
      return
    }

    // Save candidate info to context
    setInterviewData({
      candidateName,
      candidateEmail,
      startTime: new Date().toISOString(),
      answers: [],
    })

    toast({
      title: 'Mülakat Başlatılıyor',
      description: 'Bilgileriniz kaydedildi, mülakat başlatılıyor.',
    })

    // Navigate to the appropriate interview page
    if (interviewType === 'verbal') {
      navigate('/verbal-interview')
    } else if (interviewType === 'video') {
      navigate('/video-interview')
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Mülakat Seçimi</h1>
            <p className="text-xl text-gray-500">
              Lütfen katılmak istediğiniz mülakat türünü seçin
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneCall className="h-5 w-5" />
                  Sözlü Mülakat
                </CardTitle>
                <CardDescription>Mikrofon kullanarak sesli yanıtlar verin</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  Bu mülakat türünde, soruları sesli olarak yanıtlayacaksınız. Cevaplarınız
                  kaydedilecek ve daha sonra değerlendirilecektir.
                </p>
              </CardContent>
              <CardFooter>
                <Button className="w-full" onClick={() => handleStartInterview('verbal')}>
                  Sözlü Mülakat Başlat
                </Button>
              </CardFooter>
            </Card>

            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Video Mülakat
                </CardTitle>
                <CardDescription>
                  Kamera ve mikrofon kullanarak video yanıtlar verin
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  Bu mülakat türünde, soruları video kaydı yaparak yanıtlayacaksınız. Görüntü ve
                  sesiniz kaydedilecek ve daha sonra değerlendirilecektir.
                </p>
              </CardContent>
              <CardFooter>
                <Button className="w-full" onClick={() => handleStartInterview('video')}>
                  Video Mülakat Başlat
                </Button>
              </CardFooter>
            </Card>
          </div>

          <div className="text-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Ana Sayfaya Dön
            </Button>
          </div>
        </div>
      </main>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aday Bilgileri</DialogTitle>
            <DialogDescription>
              Mülakat başlatmak için lütfen bilgilerinizi girin.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Ad Soyad</Label>
                <Input
                  id="name"
                  placeholder="Adınız ve soyadınız"
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={candidateEmail}
                  onChange={(e) => setCandidateEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                İptal
              </Button>
              <Button type="submit">Devam Et</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  )
}

export default Interview
